import React from 'react';
import autobind from 'autobind-decorator';
import { Grid, Cell } from 'react-ittsu/layout';
import gql from 'fraql';

import BaseServiceModal from '../BaseServiceModal';
import Paragraph from '../Paragraph';
import SectionTitle from '../SectionTitle';
import { ProductCard } from '../../forms';
import { specsToItems, itemsToSpecs, applyChanges } from '~/pods/order/utils/services/specs-to-items';
import { priceItem, applyPrice } from '../../../utils/pricing';
import { getItemsByService, merge, getFeaturesForProduct, openIntercom } from '~/pods/order/utils';
import { createTranslate, translate as t } from '~/utils/translation';
import { EstateEnums } from '~/pods/order/utils/estate-enums';

import serviceImages from '~/pods/order/images/services';


const tt = createTranslate('pods.order.services.photo_shooting.order');
const tm = createTranslate('pods.order.components.service_modal');


class PhotoShooting extends React.Component {

  static fragments = {
    Item: gql`
      fragment _ on Item {
        id
        product {
          id @client(type: Product)
          custom
          features {
            content
            locale
          }
        }
      }
    `,
  }

  state = {
    specs: {
      standard: false,
    },
    custom: null,
    price: undefined,
  };

  componentDidUpdate(prevProps) {
    const { specs } = this.state;
    const { service, estate } = this.props;
    const { service: prevService } = prevProps;
    if (service?.id === 'photoShooting' && ! prevService) {
      const newSpecs = itemsToSpecs({ estate, service });
      const items = getItemsByService(estate).photoShooting || [];
      const customItem = items.find((item) => item.product.custom);
      const price = items != null ? items.reduce((memo, i) => memo + priceItem(i, this.props.sessionUser.currency.code, true), 0) : 0;
      this.setState({ specs: merge(specs, newSpecs), price, custom: customItem ? true : null });
    }
  }

  render() {
    const { onClickClose, isVisible, service, sessionUser, estate } = this.props;
    const { specs: { standard }, custom, price } = this.state;
    const { photoShooting } = serviceImages;
    const items = getItemsByService(estate).photoShooting || [];
    const customItem = items.find((item) => item.product.custom);
    const customFeatures = custom !== null ? getFeaturesForProduct(customItem?.product) : null;
    const isResidential = estate.projectType === EstateEnums.ProjectType.RESIDENTIAL;
    return (
      <BaseServiceModal
        isVisible={isVisible && service?.id === 'photoShooting'}
        title={service?.id ? t(`services.${service.id}`) : ''}
        images={Object.values(photoShooting)}
        price={sessionUser.addCurrency(price)}
        onClickConfirm={this._handleClickConfirm}
        confirmAsUpdate={items?.length > 0}
        onClickClose={onClickClose}
        addDisabled={price === 0}>
        <div className="childcontent">
          <Paragraph>
            {tt('description')}
          </Paragraph>
          <SectionTitle>
            {tt('select_type')}
          </SectionTitle>
          <Grid withVGutters withHGutters>
            <Cell size="1of2">
              <ProductCard
                label={tm('standard')}
                note={tm('from_price', { price: applyPrice(isResidential ? { EUR: 480, USD: 576, GBP: 450 } : { EUR: 700, USD: 840, GBP: 650 }, sessionUser) })}
                fullHeight
                formComponent="radio"
                features={[
                  {
                    text: tt('features.first'),
                  },
                  {
                    text: tt('features.second'),
                  },
                  {
                    text: tt('features.third'),
                  },
                  {
                    text: tt('features.fourth'),
                  },
                  {
                    text: tt('features.fifth'),
                  },
                ]}
                value={standard}
                onChange={this._handleClickStandard} />
            </Cell>
            <Cell size="1of2">
              <ProductCard
                name="custom"
                label={custom !== null ? `${customItem?.product.name}` : tm('custom')}
                note={custom !== null && customItem ? `${sessionUser.addCurrency(customItem?.price)}` : tm('contact_us')}
                formComponent="radio"
                fullHeight
                features={customFeatures}
                onChange={this._handleClickCustom}
                value={!! custom} />
            </Cell>
          </Grid>
        </div>
      </BaseServiceModal>
    );
  }

  @autobind
  _syncState() {
    const { estate, service } = this.props;
    const { specs } = this.state;
    const changes = specsToItems({ specs, estate, service });
    const updatedEstate = applyChanges(changes, estate);
    const items = getItemsByService(updatedEstate).photoShooting;
    const price = items != null
      ? items
          .map((item) => item.estate != null ? item : { ...item, estate: updatedEstate })
          .reduce((memo, i) => memo + priceItem(i, this.props.sessionUser.currency.code, true), 0)
      : 0;
    this.setState({ price });
    return { changes, updatedEstate, price };
  }

  @autobind
  _handleClickStandard() {
    const { specs } = this.state;
    this.setState({ specs: { standard: ! specs.standard } }, this._syncState);
  }

  @autobind
  _handleClickCustom() {
    const { custom } = this.state;
    if (custom === null) {
      openIntercom();
      this._resetSpecs(this._syncState);
    }
    else {
      const newValue = ! custom;
      this._resetSpecs(() => this.setState({ custom: newValue }, this._syncState));
    }
  }

  @autobind
  _resetSpecs(onReset) {
    this.setState({ specs: { standard: false } }, onReset);
  }

  @autobind
  _handleClickConfirm() {
    const { onModifyService, estate } = this.props;
    const { custom } = this.state;
    const extraRemove = custom === false ? [getItemsByService(estate).photoShooting.find((item) => item.product.custom)] : [];
    const { changes } = this._syncState();
    onModifyService({ ...changes, remove: [ ...changes.remove, ...extraRemove ] });
  }
}


export default PhotoShooting;
